// /reminders — per-medication reminder schedule. Stores in DB now;
// dispatch via Customer.io email + web push lands in V1.2.

import type { Medication } from '@medreview/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listMedications } from '../api/medications';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { EmptyIllustration } from '../components/senior/EmptyIllustration';
import { SeniorButton } from '../components/senior/SeniorButton';
import { SkeletonCardList } from '../components/senior/Skeleton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useMe } from '../hooks/useMe';
import { AppPage } from '../layouts/AppPage';

interface Reminder {
  id: string;
  medicationId: string;
  fireAt: string;
  channel: 'email' | 'web_push' | 'sms';
  slot: string;
  active: boolean;
}

function slotDefaults(wakeTime: string, bedTime: string): Record<string, string> {
  // Naive midpoint between wake and bedtime for noon/evening so they
  // still respect a non-9-to-5 schedule. Falls back to safe defaults.
  const toMin = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return ((h ?? 0) * 60 + (m ?? 0)) % 1440;
  };
  const toStr = (m: number) =>
    `${Math.floor(m / 60)
      .toString()
      .padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
  const wake = toMin(wakeTime);
  const bed = toMin(bedTime);
  const span = bed > wake ? bed - wake : 1440 - wake + bed;
  const noon = (wake + Math.round(span * 0.33)) % 1440;
  const evening = (wake + Math.round(span * 0.66)) % 1440;
  return {
    morning: toStr(wake),
    noon: toStr(noon),
    evening: toStr(evening),
    bedtime: toStr(bed),
    custom: toStr(wake),
  };
}

export function Reminders(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const { me } = useMe();
  const [meds, setMeds] = useState<Medication[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedMed, setSelectedMed] = useState<string>('');
  const [slot, setSlot] = useState<'morning' | 'noon' | 'evening' | 'bedtime' | 'custom'>(
    'morning',
  );
  const slotDefaultsMap = slotDefaults(me?.wakeTime ?? '08:00', me?.bedTime ?? '22:00');
  const [time, setTime] = useState('08:00');
  // Keep the time input in sync with the user's wake/bed prefs when
  // they load, or when the user changes the slot dropdown.
  // biome-ignore lint/correctness/useExhaustiveDependencies: slotDefaultsMap derived from me
  useEffect(() => {
    setTime(slotDefaultsMap[slot] ?? '09:00');
  }, [slot, me?.wakeTime, me?.bedTime]);

  const reload = async () => {
    const [medList, remRes] = await Promise.all([
      listMedications(authedFetch),
      authedFetch('/api/reminders'),
    ]);
    setMeds(medList);
    if (medList.length > 0 && !selectedMed) setSelectedMed(medList[0]?.id ?? '');
    const json = (await remRes.json()) as { ok: boolean; data?: Reminder[] };
    if (json.ok && Array.isArray(json.data)) setReminders(json.data);
    setLoading(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only fetch
  useEffect(() => {
    void reload();
  }, []);

  const addReminder = async () => {
    if (!selectedMed || busy) return;
    setBusy(true);
    try {
      await authedFetch('/api/reminders', {
        method: 'POST',
        body: JSON.stringify({
          medicationId: selectedMed,
          fireAt: time,
          slot,
        }),
      });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (r: Reminder) => {
    setBusy(true);
    try {
      await authedFetch(`/api/reminders/${r.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !r.active }),
      });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: Reminder) => {
    setBusy(true);
    try {
      await authedFetch(`/api/reminders/${r.id}`, { method: 'DELETE' });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const medName = (id: string) => meds.find((m) => m.id === id)?.name ?? '?';

  return (
    <AppPage title="Reminders" back={{ to: '/account', label: 'Account' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Stay on time</p>
          <p className="food-intro__text">
            Set a daily reminder for each medicine. Siggy will email you, ping your phone if you
            turned on push reminders in Settings, and text you if you saved a mobile number in
            Settings.
          </p>
        </div>
      </section>

      {loading ? <SkeletonCardList count={3} /> : null}

      {!loading && meds.length === 0 ? (
        <EmptyIllustration
          kind="reminders"
          title="Add medicines first"
          body="You need at least one medicine before you can set reminders."
          cta={
            <Link to="/medications/add-by-photo" className="home-hero__cta">
              Add a medicine
            </Link>
          }
        />
      ) : null}

      {!loading && meds.length > 0 ? (
        <>
          <section className="account-section">
            <h3 className="account-section__title">New reminder</h3>
            <div className="reminders-form">
              <label>
                Medicine
                <select
                  aria-label="Medicine"
                  value={selectedMed}
                  onChange={(e) => setSelectedMed(e.target.value)}
                >
                  {meds.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.strength ? ` ${m.strength}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Time of day
                <select
                  value={slot}
                  onChange={(e) => {
                    const next = e.target.value as
                      | 'morning'
                      | 'noon'
                      | 'evening'
                      | 'bedtime'
                      | 'custom';
                    setSlot(next);
                    setTime(slotDefaultsMap[next] ?? '08:00');
                  }}
                >
                  <option value="morning">Morning</option>
                  <option value="noon">Noon</option>
                  <option value="evening">Evening</option>
                  <option value="bedtime">Bedtime</option>
                  <option value="custom">Custom</option>
                </select>
              </label>
              <label>
                Time
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </label>
              <SeniorButton
                variant="primary"
                onClick={() => void addReminder()}
                disabled={busy || !selectedMed}
              >
                Add reminder
              </SeniorButton>
            </div>
          </section>

          {reminders.length > 0 ? (
            <section className="account-section">
              <h3 className="account-section__title">Your reminders</h3>
              <ul className="food-list">
                {reminders.map((r) => (
                  <li key={r.id} className="food-item">
                    <span
                      className={`food-pill ${r.active ? 'food-pill--note' : 'food-pill--limit'}`}
                    >
                      {r.fireAt}
                    </span>
                    <div>
                      <p className="food-item__name">{medName(r.medicationId)}</p>
                      <p className="food-item__reason">
                        {r.slot} • {r.channel}
                        {r.active ? '' : ' • paused'}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                      <button
                        type="button"
                        className="account-link"
                        onClick={() => void toggle(r)}
                        disabled={busy}
                        style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
                      >
                        {r.active ? 'Pause' : 'Resume'}
                      </button>
                      <button
                        type="button"
                        className="account-link"
                        onClick={() => void remove(r)}
                        disabled={busy}
                        style={{
                          background: 'transparent',
                          border: 0,
                          cursor: 'pointer',
                          color: 'var(--color-danger)',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      ) : null}
    </AppPage>
  );
}
